import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';
import MyPlan from './MyPlan/MyPlan';
import VPSInfo from './VPSInfo/VPSInfo';
import SSHAccess from './SSHAccess/SSHAccess';
import Management from './Management/Management';
import PlanDetails from './PlanDetails/PlanDetails';
import * as Styles from './VpsOciDetailPage.styles';
import ChangePlanVpsOciCard from '@/components/Cards/ChangePlanVpsOciCard';
import { differenceInMilliseconds, parseISO } from 'date-fns';
import useLocale from '@/hooks/useLocale';
import FaqProductDetails from '@/components/FrequentlyQuestions/FaqProductDetails';
import SupportForms from '@/components/SupportForms';
import { useParams } from 'react-router-dom';
import { Skeleton } from 'gatorcomponents';
import { commonActions } from '@/redux/modules';
import VpsStoreLoaderPage from './components/VpsStoreLoaderPage/VpsStoreLoaderPage';
import VpsWelcomeModal from './components/VpsWelcomeModal';
import VpsGuidedTour from './components/VpsGuidedTour';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { ENABLE_VPS_OCI_GUIDE_TOUR, ENABLE_VPS_OCI_MANAGEMENT_SO_APPS } from '@/config/GrowthBook/constants';
import { loadRebootHistory } from '@/redux/actions/productDetail';
import { useHistory } from 'react-router';
import { locale as localeRoutes } from '@/utils/locale';

import ProductTemplatesPage from './ProductTemplatesPage/ProductTemplatesPage';

const TIMER_TO_END_RESTART = 0;

const VpsOciDetailPage = () => {
  const [rebootingPercentage, setRebootingPercentage] = useState(0);
  const [formToShow, setFormToShow] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [shouldStartTour, setShouldStartTour] = useState(false);
  const [isReviewingTour, setIsReviewingTour] = useState(false);

  const { token } = useSelector(state => state.auth);
  const [tourSession, setTourSession] = useState(`${token}-${Date.now()}`);

  const enableVpsOciGuideTour = useFeatureIsOn(ENABLE_VPS_OCI_GUIDE_TOUR);
  const enableVpsOciManagementSoApps = useFeatureIsOn(ENABLE_VPS_OCI_MANAGEMENT_SO_APPS);

  const myPlanRef = useRef(null);
  const vpsInfoRef = useRef(null);
  const sshAccessRef = useRef(null);
  const managementRef = useRef(null);

  const history = useHistory();

  const dispatch = useDispatch();
  const vpsOci = useSelector(state => state.vpsOci);
  const { loading, isRebooting } = vpsOci || {};
  const { productName, vps, packageId } = (vpsOci && vpsOci.data) || {};

  const productDetail = useSelector(state => state.productDetail.historyBkp) || [];
  const { historyBkp = [] } = productDetail || {};

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: locale } = productDetailsLocale;
  const { id: hostingId } = useParams();
  const isVpsOciOnline = vpsOci.status && vpsOci.status.value === 'online';
  const canShowTour = enableVpsOciGuideTour && vpsOci.tour && vpsOci.tour.canShowTour;
  const canShowVpsOciTour = !loading && canShowTour && isVpsOciOnline;

  const [showProductTemplatesPage, setShowProductTemplatesPage] = useState(false);

  useEffect(() => {
    dispatch(vpsOciActions.data.get({ hostingId }));
  }, [dispatch, hostingId]);

  useEffect(() => {
    dispatch(vpsOciActions.rebuild.reset());
    dispatch(vpsOciActions.changeImage.reset());
  }, [dispatch]);

  const timerRef = useRef(null);
  const clearRebootingTimeout = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, []);

  const handleRebootingTimer = useCallback(() => {
    if (historyBkp && historyBkp.length > 0) {
      const leftTimerToRestart = TIMER_TO_END_RESTART - differenceInMilliseconds(new Date(), parseISO(historyBkp[0].datetime));

      const percentage = (leftTimerToRestart / TIMER_TO_END_RESTART) * 100;

      setRebootingPercentage(percentage);

      if (leftTimerToRestart > 0) {
        dispatch(vpsOciActions.reboot.loading(true));
        timerRef.current = setTimeout(() => {
          setRebootingPercentage(100);
          dispatch(vpsOciActions.reboot.loading(false));
        }, leftTimerToRestart);
      } else {
        dispatch(vpsOciActions.reboot.loading(false));
      }
    }
  }, [historyBkp, dispatch]);

  const handleShowForm = useCallback((formName) => {
    setFormToShow(formName);
  }, []);

  const handleStartTour = useCallback(() => {
    setShowWelcomeModal(false);
    setTimeout(() => {
      setShouldStartTour(true);
    }, 100);
  }, []);

  const handleReviewTour = useCallback(() => {
    setTourSession(`${token}-${Date.now()}`);
    setShouldStartTour(true);
    setIsReviewingTour(true);
  }, [token]);

  const handleTourClose = useCallback(() => {
    setShouldStartTour(false);
    setTourSession(null);
  }, []);

  const saveTourDismissed = useCallback(() => {
    !isReviewingTour && dispatch(vpsOciActions.tour.saveStep({ step: 'tour_dismissed', hostingId, session: tourSession }));
    setShowWelcomeModal(false);
    setTourSession(null);
  }, [dispatch, isReviewingTour, hostingId, tourSession]);

  useEffect(() => {
    handleRebootingTimer();
    return () => clearRebootingTimeout();
  }, [historyBkp, dispatch, clearRebootingTimeout, handleRebootingTimer]);

  useEffect(() => {
    dispatch(vpsOciActions.tour.get({ hostingId }));
  }, [dispatch, hostingId]);

  useEffect(() => {
    if (canShowVpsOciTour) {
      !tourSession && setTourSession(`${token}-${Date.now()}`);
      setShowWelcomeModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canShowVpsOciTour, token]);

  useEffect(() => {
    if (!loading && (!vpsOci || !vpsOci.data)) {
      dispatch(commonActions.notifications.set({
        label: locale.messages.vpsNotFound,
        type: 'error',
      }));
    }
  }, [vpsOci, loading, dispatch, locale]);


  const handleLoadRebootHistory = useCallback(() => {
    if (hostingId) {
      dispatch(loadRebootHistory(hostingId));
    }
  }, [hostingId, dispatch]);

  const handleGoToUpgradePlanPage = useCallback(() => {
    handleShowForm('upgradePlan');
  }, [handleShowForm]);

  const handleGoToChangePlanPage = useCallback(() => {
    if (enableVpsOciManagementSoApps) {
      history.push(`${localeRoutes('routes.products')}${localeRoutes('routes.productdetail')}/${hostingId}${localeRoutes('routes.templates')}`);
      return;
    }

    handleShowForm('changePlan');
  }, [enableVpsOciManagementSoApps, history, hostingId, handleShowForm]);

  useEffect(() => {
    handleLoadRebootHistory();
  }, [handleLoadRebootHistory]);

  if (isRebooting) {
    return (
      <VpsStoreLoaderPage
        progress={rebootingPercentage}
        title={locale.myPlan.feedback.loading}
      />
    );
  }

  if (productName) {
    const getProductNamePackageVPSOci = (productName && vps.domain) ? `${productName} - ${vps.domain}` : '';

    if (formToShow === 'upgradePlan') {
      return (
        <SupportForms
          serviceType={{
            title: locale.planDetails.form.title,
            onSuccess: () => setFormToShow(''),
            onBackForm: () => setFormToShow(''),
            lockedOptions: getProductNamePackageVPSOci !== '',
            defaultValues: {
              defaultSelectedProductOption: getProductNamePackageVPSOci,
              defaultSelectedSector: 'billing',
              defaultSelectedNeed: locale.planDetails.form.defaultSelectedNeed,
            },
          }}
        />
      );
    }

    if (formToShow === 'changePlan') {
      return (
        <SupportForms
          serviceType={{
            title: locale.changePlan.form.title,
            onSuccess: () => setFormToShow(''),
            onBackForm: () => setFormToShow(''),
            lockedOptions: getProductNamePackageVPSOci !== '',
            defaultValues: {
              defaultSelectedProductOption: getProductNamePackageVPSOci,
              defaultSelectedSector: 'billing',
              defaultSelectedNeed: locale.changePlan.form.defaultSelectedNeed,
            },
          }}
        />
      );
    }
  }

  if (loading) {
    return (
      <Styles.Container data-testid="vpsoci-detail-page-loading">
        <Styles.Row>
          <Skeleton width="100%" height="116px" />
        </Styles.Row>
        <Styles.Row>
          <Skeleton width="100%" height="470px" />
          <Skeleton width="100%" height="470px" />
        </Styles.Row>
        <Styles.Row>
          <Skeleton width="100%" height="178px" />
        </Styles.Row>
        <Styles.Row>
          <Skeleton width="100%" height="144px" />
        </Styles.Row>
        <Styles.Row>
          <Skeleton width="100%" height="116px" />
          <Skeleton width="100%" height="116px" />
        </Styles.Row>
      </Styles.Container>
    );
  }

  if (!vpsOci || !vpsOci.data || Object.keys(vpsOci).length === 0 || Object.keys(vpsOci.data).length === 0) return null;

  if (showProductTemplatesPage) {
    return (
      <ProductTemplatesPage
        setShowProductTemplatesPage={setShowProductTemplatesPage}
      />
    );
  }

  return (
    <Styles.Container data-testid="vpsoci-detail-page">

      <Styles.Row data-testid="vpsoci-detail-page-myplan">
        <MyPlan ref={myPlanRef} />
      </Styles.Row>

      <Styles.Row data-testid="vpsoci-detail-page-vpsinfo-sshaccess">
        <VPSInfo ref={vpsInfoRef} />
        <SSHAccess ref={sshAccessRef} />
      </Styles.Row>

      <Styles.Row data-testid="vpsoci-detail-page-management">
        <Management
          ref={managementRef}
          setShowProductTemplatesPage={setShowProductTemplatesPage}
        />
      </Styles.Row>

      <Styles.Row data-testid="vpsoci-detail-page-plan-details">
        <PlanDetails onGoToUpgradePlanPage={handleGoToUpgradePlanPage} />
      </Styles.Row>

      <Styles.Row data-testid="vpsoci-detail-page-change-plan-card-faq">
        <ChangePlanVpsOciCard
          locales={{
            title: locale.changePlan.hireBanner.title,
            description: locale.changePlan.hireBanner.description,
            buttonLabel: locale.changePlan.hireBanner.button,
          }}
          onGoToChangePlanPage={handleGoToChangePlanPage}
        />
        {packageId && (
          <FaqProductDetails
            packageid={packageId}
            onSecondaryAction={enableVpsOciGuideTour && handleReviewTour}
            secondaryButtonLabel={locale.helpLinks.tourButton}
            showSameWithoutQuestions
            testId="vpsoci-detail-page-faq-product-details"
          />
        )}
      </Styles.Row>
      {showWelcomeModal && (
        <VpsWelcomeModal
          onClose={saveTourDismissed}
          onStartTour={handleStartTour}
          tourSession={tourSession}
        />
      )}
      <VpsGuidedTour
        myPlanRef={myPlanRef.current}
        vpsInfoRef={vpsInfoRef.current}
        sshAccessRef={sshAccessRef.current}
        managementRef={managementRef.current}
        onClose={handleTourClose}
        shouldStart={shouldStartTour}
        isReviewingTour={isReviewingTour}
        saveTourDismissed={saveTourDismissed}
        tourSession={tourSession}
      />
    </Styles.Container>
  );
};

export default VpsOciDetailPage;
